import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const Vehicles = ({ status }) => {
  const [vehicles, setVehicles] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  
  const [filters, setFilters] = useState({
    fromDtTm: '',
    toDtTm: '',
    active: status === 'active' ? true : status === 'inactive' ? false : null
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    fetchVehicles();
  }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchVehicles = async () => {
    const params = new URLSearchParams();
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.fromDtTm) params.append('fromDtTm', filters.fromDtTm);
    if (filters.toDtTm) params.append('toDtTm', filters.toDtTm);
    if (filters.active !== null) params.append('active', filters.active);
    
    const endpoint = `/api/vehicles/filter?${params.toString()}`;
    const { data } = await get(endpoint);
    
    if (data) {
      let filteredData = data.content || [];
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(v => 
          v.brand?.toLowerCase().includes(term) ||
          v.color?.toLowerCase().includes(term) ||
          v.plates?.toLowerCase().includes(term) ||
          v.other?.toLowerCase().includes(term)
        );
      }
      setVehicles(filteredData);
      setPageInfo({
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this vehicle?')) {
      const { error } = await del(`/api/vehicles/${id}`);
      if (!error) fetchVehicles();
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVehicles();
  };

  const clearFilters = () => {
    setFilters({
      fromDtTm: '', toDtTm: '',
      active: status === 'active' ? true : status === 'inactive' ? false : null
    });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Brand', accessor: 'brand' },
    { header: 'Color', accessor: 'color' },
    { header: 'Plates', accessor: 'plates' },
    { header: 'Other', accessor: 'other' },
    { header: 'Started At', accessor: (row) => row.startedAt ? new Date(row.startedAt).toLocaleDateString() : '-' },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      header: 'Trips', 
      accessor: (row) => (
        <button
          onClick={() => navigate(`/vehicles/${row.id}`, { state: { tab: 'trips' } })}
          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
        >
          View Trips
        </button>
      )
    },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/vehicles/${row.id}`)}
            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => navigate(`/vehicles/edit/${row.id}`)}
                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDelete(row.id)}
                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
				title={row.isActive ? 'Deactivate' : 'Delete'}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Vehicles {status && `- ${status.charAt(0).toUpperCase() + status.slice(1)}`}
        </h1>
        {isAdmin && (
          <button
            onClick={() => navigate('/vehicles/add')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Vehicle</span>
          </button>
        )}
      </div>

      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by brand, color, plates..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-700 dark:text-indigo-300' : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.fromDtTm || filters.toDtTm || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" />
            Date Range Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                From Date
              </label>
              <input
                type="datetime-local"
                name="fromDtTm"
                value={filters.fromDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                To Date
              </label>
              <input
                type="datetime-local"
                name="toDtTm"
                value={filters.toDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 self-center">Quick dates:</span>
            <button
              onClick={() => {
                const today = new Date();
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: yesterday.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: weekAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: monthAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
            >
              Last 30 Days
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 self-center">Quick status:</span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: true }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === true 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
              }`}
            >
              Active Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: false }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === false 
                  ? 'bg-red-500 text-white' 
                  : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
              }`}
            >
              Inactive Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: null }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === null 
                  ? 'bg-gray-500 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              All Vehicles
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400 flex justify-between items-center">
        <span>Showing {vehicles.length} of {pageInfo.totalElements} vehicles</span>
        {filters.active === true && (
          <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs">
            Active Only
          </span>
        )}
        {filters.active === false && (
          <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs">
            Inactive Only
          </span>
        )}
      </div>

      <DataTable 
        columns={columns} 
        data={vehicles} 
        pageInfo={pageInfo} 
        onPageChange={(page) => setPageInfo({...pageInfo, currentPage: page})} 
      />
    </div>
  );
};

export default Vehicles;